import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Pagination from '../UI/Pagination';
import { backendUrl } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import styles from './GameResultsBox.module.css';
import refresh from '../../images/login-page/refresh.png';

axios.defaults.withCredentials = true;

const formatDate = dateString => {
    try {
        const date = new Date(dateString);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    } catch (err) {
        return 'Invalid date';
    }
};
const fmtPrice = pricing => `${Number(pricing?.totalPrice ?? 0)} ${(pricing?.currency || 'credits') === 'credits' ? 'cr' : pricing?.currency}`;

const GameResultsBox = () => {
    const { isLoggedIn, isAdmin, loading: authLoading } = useAuth();
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('completed');
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });

    const endpoint = isAdmin
        ? `${backendUrl}/api/history/all`
        : `${backendUrl}/api/history/my`;

    const fetchGames = useCallback(async (tab = activeTab, page = 1, limit = pagination.limit) => {
        if (!isLoggedIn) {
            setGames([]);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = {
                page,
                limit,
                completed: tab === 'completed',
            };
            const res = await axios.get(endpoint, { params });
            const data = Array.isArray(res.data?.data) ? res.data.data : [];
            const nextPagination = res.data?.pagination ?? {
                total: data.length,
                page,
                limit,
                pages: Math.max(1, Math.ceil(data.length / limit)),
            };

            setGames(data);
            setPagination({
                page: nextPagination.page,
                pages: nextPagination.pages,
                total: nextPagination.total,
                limit: nextPagination.limit,
            });
        } catch (err) {
            setGames([]);
            setError(err.response?.data?.message || 'Failed to load game results');
        } finally {
            setLoading(false);
        }
    }, [activeTab, endpoint, isLoggedIn, pagination.limit]);

    useEffect(() => {
        if (authLoading) return;
        fetchGames(activeTab, 1, pagination.limit);
    }, [authLoading, activeTab, pagination.limit, fetchGames]);

    const handleTabChange = tab => {
        setActiveTab(tab);
        setGames([]);
        setPagination(prev => ({ ...prev, page: 1, pages: 1, total: 0 }));
    };

    if (authLoading) {
        return (
            <div className={styles.boxContent}>
                <div className={styles.noResults}>Checking login…</div>
            </div>
        );
    }

    if (!isLoggedIn) {
        return (
            <div className={styles.boxContent}>
                <div className={styles.tabs}>
                    <button className={`${styles.tabButton} ${styles.activeTab}`} type="button">
                        Results
                    </button>
                </div>
                <div className={styles.noResults}>
                    Sign in to view your running games and completed results.
                    <div style={{ marginTop: 16 }}>
                        <Link
                            to="/auth/login"
                            style={{
                                display: 'inline-block',
                                padding: '10px 16px',
                                borderRadius: 8,
                                color: '#00d4ff',
                                textDecoration: 'none',
                                border: '1px solid rgba(0,212,255,0.35)',
                                background: 'rgba(0,212,255,0.08)',
                                fontStyle: 'normal',
                            }}
                        >
                            Go to Login
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.boxContent}>
            <div className={styles.tabs}>
                <button
                    type="button"
                    className={`${styles.tabButton} ${activeTab === 'completed' ? styles.activeTab : ''}`}
                    onClick={() => handleTabChange('completed')}
                >
                    Completed
                    <span className={styles.tabCount}>{activeTab === 'completed' ? pagination.total : '...'}</span>
                </button>
                <button
                    type="button"
                    className={`${styles.tabButton} ${activeTab === 'running' ? styles.activeTab : ''}`}
                    onClick={() => handleTabChange('running')}
                >
                    Running
                    <span className={styles.tabCount}>{activeTab === 'running' ? pagination.total : '...'}</span>
                </button>
                <div className={styles.refresh}>
                    <img
                        src={refresh}
                        alt="Refresh"
                        onClick={() => fetchGames(activeTab, pagination.page, pagination.limit)}
                    />
                </div>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    {error}
                </div>
            )}

            {loading ? (
                <div className={styles.loading}>Loading results…</div>
            ) : games.length === 0 ? (
                <div className={styles.noResults}>
                    {activeTab === 'completed'
                        ? 'No completed games found for your account.'
                        : 'No running games found for your account.'}
                </div>
            ) : (
                <>
                    <div className={styles.tableWrapper}>
                        <table className={styles.resultsTable}>
                            <thead>
                                <tr>
                                    <th>Room</th>
                                    <th>Players</th>
                                    <th>Password</th>
                                    <th>Price</th>
                                    <th>{activeTab === 'completed' ? 'Winner' : 'Status'}</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {games.map((game, index) => (
                                    <tr key={game._id || game.roomId || index}>
                                        <td>
                                            {game.roomCode ? (
                                                <div style={{ color: '#ffb300', fontWeight: 700, marginBottom: 4 }}>
                                                    {game.roomCode}
                                                </div>
                                            ) : null}
                                            <div style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace', fontSize: 12 }}>
                                                {game.roomId || '—'}
                                            </div>
                                        </td>
                                        <td>
                                            {Array.isArray(game.players) && game.players.length > 0
                                                ? game.players.map(player => player?.name || player).filter(Boolean).join(', ')
                                                : '—'}
                                        </td>
                                        <td>
                                            {game.password
                                                ? <span className={styles.turnTag}>Password set</span>
                                                : <span className={styles.turnTag}>None</span>}
                                        </td>
                                        <td>{fmtPrice(game.pricing)}</td>
                                        <td>
                                            {activeTab === 'completed'
                                                ? <span className={styles.winnerTag}>{game.winner || '—'}</span>
                                                : <span className={styles.turnTag}>Live</span>}
                                        </td>
                                        <td>{formatDate(game.createdAt || game.date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ padding: '0 14px 14px' }}>
                        <Pagination
                            page={pagination.page}
                            pages={pagination.pages}
                            total={pagination.total}
                            limit={pagination.limit}
                            onPage={page => fetchGames(activeTab, page, pagination.limit)}
                            onLimit={limit => {
                                setPagination(prev => ({ ...prev, limit }));
                                fetchGames(activeTab, 1, limit);
                            }}
                        />
                    </div>
                </>
            )}

            <div className={styles.moreResults}>
                <Link
                    to="/ui/results"
                    style={{
                        color: 'inherit',
                        textDecoration: 'none',
                        display: 'inline-block',
                    }}
                >
                    <span>Open full results page</span>
                </Link>
            </div>
        </div>
    );
};

export default GameResultsBox;
